<template>
  <div class="animated fadeIn">
    <div class="row">
      <div class="col-xl">
        <b-card>
          <template slot="header">
            <survey-filter v-model="surveyFilter" :disabled="isExporting" @input="loadData" />

            <div class="form-row">
              <div class="form-group col-md-4">
                <search-input v-model.trim="searchText"
                            :placeholder="$t('Webportal.SearchTextFilter.Placeholder')"
                            :disabled="isExporting"
                            @change="loadData">
                </search-input>
              </div>

              <div class="form-group col-md-auto ml-auto">
                <pagination ref="pagination"
                  v-model="selectedPage"
                  :totalCount="totalCount"
                  :selectedPageSize.sync="selectedPageSize"
                  :disabled="isExporting"
                  :showAllRowsToolTip="displayMode !== DisplayMode.TopLevelRevisions && displayMode !== DisplayMode.TopLevelImprovements ? $t('Webportal.ShowAllChecks') : null"
                  @input="loadDataInternal('input')" />
              </div>

              <div class="form-group col-md-auto">
                <b-button-group v-if="userHasActionRevisePermission() || userHasActionImprovementPermission()">
                  <b-dropdown right :variant="displayMode !== DisplayMode.None ? 'primary' : 'outline-primary'"
                              v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Actions.SurveyRevisionExplorer.ShowRevisions')">
                    <template slot="button-content">
                      <font-awesome-icon :icon="['far', 'comment-exclamation']" fixed-width />
                    </template>
                    <b-dropdown-header>{{ $t('Webportal.Actions.SurveyRevisionExplorer.ShowRevisions.Description') }}</b-dropdown-header>
                    <b-dropdown-item
                      v-if="userHasActionRevisePermission()"
                      :active="displayMode === DisplayMode.NestedRevisions"
                      @click="changeDisplayMode(DisplayMode.NestedRevisions)">
                      <font-awesome-icon :icon="['far', 'indent']" fixed-width />
                      {{ $t('Webportal.Actions.SurveyRevisionExplorer.ListOfSingleReports') }}
                    </b-dropdown-item>
                    <b-dropdown-item
                      v-if="userHasActionRevisePermission()"
                      :active="displayMode === DisplayMode.TopLevelRevisions"
                      @click="changeDisplayMode(DisplayMode.TopLevelRevisions)">
                      <font-awesome-icon :icon="['far', 'align-justify']" fixed-width />
                      {{ $t('Webportal.Actions.SurveyRevisionExplorer.ListOfRevisions') }}
                    </b-dropdown-item>
                    <b-dropdown-item
                      v-if="userHasActionImprovementPermission()"
                      :active="displayMode === DisplayMode.TopLevelImprovements"
                      @click="changeDisplayMode(DisplayMode.TopLevelImprovements)">
                      <font-awesome-icon :icon="['far', 'list']" fixed-width />
                      {{ $t('Webportal.Actions.SurveyImprovementExplorer.ListOfImprovements') }}
                    </b-dropdown-item>
                    <b-dropdown-item
                      :active="displayMode === DisplayMode.None"
                      @click="changeDisplayMode(DisplayMode.None)">
                      <font-awesome-icon :icon="['far', 'ban']" fixed-width />
                      {{ $t('Webportal.Actions.SurveyRevisionExplorer.ShowRevisions.None') }}
                    </b-dropdown-item>
                  </b-dropdown>
                  <b-button-group v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Actions.SurveyRevisionExplorer.OnlyShowChecksWithRevisions')">
                    <b-button :pressed.sync="onlyHasRevisions" :variant="displayMode !== DisplayMode.NestedRevisions ? 'primary' : 'outline-primary'" class="border-left"
                              :disabled="displayMode !== DisplayMode.NestedRevisions"
                              @click="loadData">
                      <font-awesome-icon :icon="['far', 'filter']" size="sm" />
                    </b-button>
                  </b-button-group>
                  <b-button-group v-show="displayMode === DisplayMode.TopLevelRevisions" v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.DownloadCheckRevisions')">
                    <b-button
                              variant="primary" class="border-left"
                              :disabled="isExporting"
                              @click="downloadCheckRevisions()">
                      <font-awesome-icon icon="file-excel" />
                    </b-button>
                  </b-button-group>
                  <b-button-group v-if="userHasActionImprovementPermission()" v-show="displayMode === DisplayMode.TopLevelImprovements" v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.DownloadBonusReport')">
                    <b-button variant="primary" class="border-left"
                              :disabled="isExporting || surveyFilter.computedSurveyIds == null || surveyFilter.computedSurveyIds.length == 0"
                              @click="downloadBonusReport()">
                      <font-awesome-icon icon="file-excel" />
                    </b-button>
                  </b-button-group>
                </b-button-group>
                
                <b-button-group v-if="userHasReleaseCheckPermission">
                  <b-button-group v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.ReleaseChecks')">
                    <b-button variant="primary" 
                              :disabled="releaseChecksDisabled || this.displayMode !== DisplayMode.None"
                              @click="releaseSelectedChecks()">
                      <span class="text-nowrap">
                        <font-awesome-icon :icon="['fas', 'user']" />&nbsp;
                        <font-awesome-icon :icon="['fas', 'check']" />
                      </span>
                    </b-button>
                  </b-button-group>
                  <b-button-group v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.OnlyShowUnreleasedChecks')">
                    <b-button :pressed.sync="onlyUnreleased" 
                              :variant="this.displayMode !== DisplayMode.None ? 'primary' : 'outline-primary'" class="border-left"
                              :disabled="this.displayMode !== DisplayMode.None"
                              @click="loadData">
                      <font-awesome-icon :icon="['far', 'filter']" size="sm" />
                    </b-button>
                  </b-button-group>
                </b-button-group>

                <b-button-group>
                  <b-button-group v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.DownloadDealerReports')">
                    <b-button variant="primary" class="btn-head-custom"
                              :disabled="isExporting || downloadDealerReportsDisabled"
                              @click="downloadDealerReports()">
                      <font-awesome-icon icon="file-pdf" />
                    </b-button>
                  </b-button-group>
                  <b-dropdown id="ReportLanguageDropdown" right variant="outline-primary"
                              v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.CheckReportLanguage.Description')">
                    <template slot="button-content">
                      <font-awesome-icon :icon="['far', useSurveyDefaultLocalization ? 'globe' : 'language']" fixed-width />
                    </template>
                    <b-dropdown-item @click="useSurveyDefaultLocalization = true">
                      <font-awesome-icon :icon="['far', 'globe']" fixed-width />
                      {{ $t('Webportal.SurveyDefaultLanguage') }}
                      <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.SurveyDefaultLanguage.Description')">
                        <font-awesome-icon :icon="['fas', 'info-circle']" />
                      </span>
                    </b-dropdown-item>
                    <b-dropdown-item @click="useSurveyDefaultLocalization = false">
                      <font-awesome-icon :icon="['far', 'language']" fixed-width />
                      {{ $t('Webportal.LocationLanguage') }}
                      <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.LocationLanguage.Description')">
                        <font-awesome-icon :icon="['fas', 'info-circle']" />
                      </span>
                    </b-dropdown-item>
                  </b-dropdown>
                </b-button-group>
              </div>
            </div>
          </template>

          <div v-bind:class="{ spinner: isLoading || isExporting }" style="min-height:300px;">
            <!-- Important: use @click.stop and @click.native.stop for action items inside table
                https://bootstrap-vue.js.org/docs/components/table/ -->
            <b-table id="check-table" small hover responsive :fixed="browserIsIE"
                    :foot-clone="items && items.length >= 5"
                    :items="items"
                    :fields="fields"
                    no-local-sorting
                    @sort-changed="sortChanged"
                    @row-clicked="rowClicked">
              <template v-slot:head(Select)>
                <b-form-checkbox v-model="toggleSelectionChecked"
                                :indeterminate="toggleSelectionIndeterminate"
                                style="min-height:1.0rem"
                                @click.native.stop
                                @change="toggleSelection"
                                v-b-tooltip.hover.nofade="toolTipDelay"
                                :title="$t('Webportal.SelectAll')" />
              </template>
              <template v-slot:head(SurveyKpi1Result)="data">
                <span v-if="!data.isFoot" id="tooltip-kpi1-circle">
                  <font-awesome-icon :icon="['fas', 'info-circle']" />
                </span>
                {{ $t('Webportal.Administration.SurveyKpi1') }}

                <b-tooltip v-if="!data.isFoot" id="tooltip-kpi1" no-fade :delay="toolTipDelay.delay" target="tooltip-kpi1-circle">
                  <p class="m-0" v-for="survey in kpiToolTipItems" :key="survey.SurveyId">
                    {{ survey.SurveyTitle }}: <strong>{{ survey.SurveyKpi1Name }}</strong>
                  </p>
                </b-tooltip>
              </template>
              <template v-slot:head(SurveyKpi2Result)="data">
                <span v-if="!data.isFoot" id="tooltip-kpi2-circle">
                  <font-awesome-icon :icon="['fas', 'info-circle']" />
                </span>
                {{ $t('Webportal.Administration.SurveyKpi2') }}

                <b-tooltip v-if="!data.isFoot" id="tooltip-kpi2" no-fade :delay="toolTipDelay.delay" target="tooltip-kpi2-circle">
                  <p v-for="survey in kpiToolTipItems" :key="survey.SurveyId">
                    {{ survey.SurveyTitle }}: <strong>{{ survey.SurveyKpi2Name }}</strong>
                  </p>
                </b-tooltip>
              </template>

              <template slot="top-row">
                <template v-if="displayMode === DisplayMode.TopLevelRevisions">
                  <td>
                    <search-input small v-model.trim="columnFilter.SurveyRevision.SurveyRevisionVisibleId" @change="loadData"
                                  :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                  </td>
                  <td>
                    <search-input small v-model.trim="columnFilter.SurveyRevision.CreatedAt" @change="loadData"
                                  :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                  </td>
                  <td>
                    <search-input small v-model.trim="columnFilter.SurveyRevision.CalendarWeek" @change="loadData"
                                  :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                  </td>
                  <td v-if="isDueDateVisible">
                    <search-input small v-model.trim="columnFilter.SurveyRevision.DueDate" @change="loadData"
                                  :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                  </td>
                  <td colspan="2">
                    <search-select small v-model="columnFilter.SurveyRevision.StatusId"
                                  :options="surveyRevisionStatusOptions" @change="loadData"
                                  :placeholder="$t('Webportal.SearchValueFilter.Placeholder')" />
                  </td>
                </template>
                <template v-else-if="displayMode === DisplayMode.TopLevelImprovements">
                  <td>
                    <search-input small v-model.trim="columnFilter.SurveyImprovement.SurveyImprovementVisibleId" @change="loadData"
                                  :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                  </td>
                  <td>
                    <search-input small v-model.trim="columnFilter.SurveyImprovement.CreatedAt" @change="loadData"
                                  :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                  </td>
                  <td>
                    <search-input small v-model.trim="columnFilter.SurveyImprovement.CalendarWeek" @change="loadData"
                                  :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                  </td>
                  <td colspan="2">
                    <search-select small v-model="columnFilter.SurveyImprovement.StatusId"
                                  :options="surveyImprovementStatusOptions" @change="loadData"
                                  :placeholder="$t('Webportal.SearchValueFilter.Placeholder')" />
                  </td>
                </template>
                <td v-else></td>
                <td>
                  <search-input small v-model.trim="columnFilter.Survey.Title" @change="loadData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td>
                  <search-input small v-model.trim="columnFilter.SurveyInstance.ExternalSurveyInstanceId" @change="loadData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td>
                  <search-input small v-model.trim="columnFilter.ClientLocation.ClientLocationIdOnClientSite" @change="loadData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td>
                  <search-input small v-model.trim="columnFilter.ClientLocation.FullAddress" @change="loadData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <template v-if="displayMode !== DisplayMode.TopLevelRevisions && displayMode !== DisplayMode.TopLevelImprovements">
                  <td v-if="isCalendarWeekFieldVisible">
                    <search-input small v-model.trim="columnFilter.SurveyInstance.CalendarWeek" @change="loadData"
                                  :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                  </td>
                </template>
                <td v-show="displayMode !== DisplayMode.TopLevelRevisions && displayMode !== DisplayMode.TopLevelImprovements && isOverallScoreVisible">
                  <search-input small v-model.trim="columnFilter.SurveyInstance.Result" @change="loadData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td v-show="displayMode !== DisplayMode.TopLevelRevisions && displayMode !== DisplayMode.TopLevelImprovements && isKpi1Visible">
                  <search-input small v-model.trim="columnFilter.SurveyKpi1Result" @change="loadData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td v-show="displayMode !== DisplayMode.TopLevelRevisions && displayMode !== DisplayMode.TopLevelImprovements && isKpi2Visible">
                  <search-input small v-model.trim="columnFilter.SurveyKpi2Result" @change="loadData"
                                :placeholder="$t('Webportal.SearchTextFilter.Placeholder')" />
                </td>
                <td v-if="isStatusFieldVisible" colspan="2">
                  <search-select small v-model="columnFilter.SurveyInstance.SurveyInstanceStatusId"
                                :options="surveyInstanceStatusOptions" @change="loadData"
                                :placeholder="$t('Webportal.SearchValueFilter.Placeholder')" />
                </td>
                <td v-else></td>
              </template>

              <!-- Survey Instance columns -->
              <template v-slot:cell(Select)="data">
                <b-form-checkbox v-model="data.item.Selected"
                                :disabled="!isSurveyInstanceProcessed(data.item)"
                                @click.native.stop
                                @change="updateToggleSelection" />
              </template>
              <template v-slot:[`cell(ClientLocation.ClientLocationIdOnClientSite)`]="data">
                {{ data.value }}<br>
                <span v-b-tooltip.hover.nofade="toolTipDelay" :title="getLocationActiveText()">
                  <font-awesome-icon v-if="data.item.ClientLocation.IsActive"
                                    :icon="['far', 'lightbulb-on']" />
                </span>
                <span v-b-tooltip.hover.nofade="toolTipDelay" :title="getLocationInactiveText()">
                  <font-awesome-icon v-if="!data.item.ClientLocation.IsActive"
                                    :icon="['far', 'lightbulb-slash']" />
                </span>
              </template>
              <template v-slot:cell(ClientLocation)="data">
                {{ data.value.Name }}<br>
                {{ data.value.Address }}<br>
                {{ data.value.City }}, {{ data.value.PostalCode }}
              </template>
              <template v-slot:[`cell(SurveyInstance.TimeStampSurveyStart)`]="data">
                <template v-if="isMsmUser || data.item.ShowCheckDate || data.item.ShowCheckTime">
                  {{ calendarWeekAndDateFormatted(data.item) }}
                </template>
              </template>
              <template v-slot:[`cell(SurveyInstance.Result)`]="data">
                {{ data.item.SurveyInstance.Result != null ? $n(data.item.SurveyInstance.Result) + '%' : '' }}
              </template>
              <template v-slot:cell(SurveyKpi1Result)="data">
                {{ data.item.SurveyKpi1Result != null ? $n(data.item.SurveyKpi1Result) + '%' : '' }}
              </template>
              <template v-slot:cell(SurveyKpi2Result)="data">
                {{ data.item.SurveyKpi2Result != null ? $n(data.item.SurveyKpi2Result) + '%' : '' }}
              </template>
              <template v-slot:[`cell(SurveyInstance.SurveyInstanceStatusId)`]="data">
                <template v-if="isMsmUser || data.item.ShowCheckStatus">
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="getSurveyInstanceStatusText(data.value)">
                    <font-awesome-icon
                      :icon="getSurveyInstanceStatusIcon(data.value)"
                      class="ml-1" fixed-width />
                  </span>
                  <template v-if="isReleaseCheckEnabled(data.item) && userHasReleaseCheckPermission">
                    <span v-if="isCheckReleased(data.item)"
                          v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.ReleasedCheck.Description')">
                      <font-awesome-icon 
                        :icon="['fas', 'user-check']"
                        class="ml-1" fixed-width />
                    </span>
                    <span v-else
                          v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.UnreleasedCheck.Description')">
                      <font-awesome-icon 
                        :icon="['fas', 'user-times']"
                        class="ml-1 text-danger" fixed-width />
                    </span>
                  </template>
                  
                  <span 
                    v-if="data.item.SurveyInstance.SurveyInstanceStatusId == 220"
                    v-b-tooltip.hover.nofade="toolTipDelay" 
                    :title="data.item.SurveyInstance.IsReadByUser ? $t('Webportal.Read') : $t('Webportal.Unread')"
                    @click="toggleSurveyInstanceReadByUser(data.item.SurveyInstance)"
                  >
                    <font-awesome-icon
                      v-if="data.item.SurveyInstance.IsReadByUser" 
                      icon="envelope-open"
                      class="ml-1" fixed-width
                      style="color: #AAA;" />
                    <font-awesome-icon
                      v-else
                      icon="envelope"
                      class="ml-1" fixed-width />
                  </span>
                  <template v-if="isReleaseCheckEnabled(data.item) && userHasReleaseCheckPermission">
                      <b-badge v-if="isSurveyInstanceProcessed(data.item) && !isCheckReleased(data.item)"
                        v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.UnreleasedCheck.Description')"
                        variant="danger">
                        <font-awesome-icon :icon="['far', 'search']" /> {{ $t('Webportal.PleaseReview') }}
                      </b-badge>
                  </template>
                </template>
              </template>
              <template v-slot:cell(Action)="data">
                <b-button-group class="position-static position-static-custom" vertical>
                  <b-button-group v-if="!isTargetGroupSelectionVisible"
                    v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.ShowPreview')"
                    class="border-bottom">
                    <b-button variant="primary" size="sm"
                              @click.stop="showPreview(data.item, useSurveyDefaultLocalization)"
                              :disabled="!isSurveyInstanceProcessed(data.item)">
                      <span class="text-nowrap">
                        <font-awesome-icon icon="eye" />&nbsp;
                        <span :class="['flag-icon', `flag-icon-${getSurveyInstanceLanguage(data.item, useSurveyDefaultLocalization)}`]" />
                      </span>
                    </b-button>
                  </b-button-group>
                  <b-dropdown v-else
                              split right variant="primary" size="sm" class="border-bottom" boundary="window"
                              @click.stop="showPreview(data.item, useSurveyDefaultLocalization)"
                              v-b-tooltip.hover.nofade="toolTipDelay"
                              :title="$t('Webportal.ShowPreview')"
                              :disabled="!isSurveyInstanceProcessed(data.item)">
                    <template slot="button-content">
                      <span class="text-nowrap">
                        <font-awesome-icon icon="eye" fixed-width />&nbsp;
                        <span :class="['flag-icon', `flag-icon-${getSurveyInstanceLanguage(data.item, useSurveyDefaultLocalization)}`]" />
                      </span>
                    </template>
                    <b-dropdown-text style="width: 300px;">{{ $t('Webportal.AvailableCheckReports') }}</b-dropdown-text>
                    <b-dropdown-header>{{ $t('Webportal.SurveyDefaultLanguage') }}</b-dropdown-header>
                    <b-dropdown-item v-for="targetGroup in data.item.TargetGroups" :key="`SDL${targetGroup.TargetGroup}`"
                                     @click="showPreview(data.item, true, targetGroup.TargetGroup)">
                      <span :class="['flag-icon', `flag-icon-${getSurveyInstanceLanguage(data.item, true)}`]" />
                      {{ $t('Webportal.TargetGroup') }} {{ targetGroup.TargetGroup }} ({{targetGroup.GroupName}})
                    </b-dropdown-item>
                    <b-dropdown-header>{{ $t('Webportal.LocationLanguage') }}</b-dropdown-header>
                    <b-dropdown-item v-for="targetGroup in data.item.TargetGroups" :key="`LL${targetGroup.TargetGroup}`"
                                     @click="showPreview(data.item, false, targetGroup.TargetGroup)">
                      <span :class="['flag-icon', `flag-icon-${getSurveyInstanceLanguage(data.item, false)}`]" />
                      {{ $t('Webportal.TargetGroup') }} {{ targetGroup.TargetGroup }} ({{targetGroup.GroupName}})
                    </b-dropdown-item>
                  </b-dropdown>
  
                  <b-button-group v-if="isReleaseCheckEnabled(data.item) && !isCheckReleased(data.item) && userHasReleaseCheckPermission"
                                  v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.ReleaseCheck')"
                                  class="border-top border-bottom"
                                  @click="releaseCheck(data.item)">
                    <b-button variant="primary" size="sm"
                              :disabled="!isSurveyInstanceProcessed(data.item)">
                      <span class="text-nowrap">
                        <font-awesome-icon :icon="['fas', 'user']" />&nbsp;
                        <font-awesome-icon :icon="['fas', 'check']" />
                      </span>
                    </b-button>
                  </b-button-group>

                  <b-button-group v-if="displayMode !== DisplayMode.TopLevelRevisions && displayMode !== DisplayMode.TopLevelImprovements &&
                                        userHasActionRevisePermission() && !userHasActionReviseReadOnlyPermission() && isSurveyRevisionAllowed(data.item.SurveyInstance)"
                                  v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Actions.SurveyRevision.Create')"
                                  class="border-top">
                    <b-button :disabled="!isSurveyInstanceProcessed(data.item)"
                              variant="primary" size="sm" @click="createSurveyRevision(data.item)">
                      <span class="text-nowrap">
                        <font-awesome-icon :icon="['fas', 'comment-exclamation']" size="lg" />&nbsp;
                        <font-awesome-icon :icon="['far', 'plus']" />
                      </span>
                    </b-button>
                  </b-button-group>

                  <!-- <b-button-group v-if="displayMode !== DisplayMode.TopLevelRevisions && displayMode !== DisplayMode.TopLevelImprovements &&
                                        userHasActionImprovementPermission() && isSurveyImprovementAllowed(data.item.SurveyInstance)"
                                  v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Actions.SurveyImprovement.Create')"
                                  class="border-top">
                    <b-button :disabled="!isSurveyInstanceProcessed(data.item)"
                              variant="primary" size="sm" @click="createSurveyImprovement(data.item)">
                      <span class="text-nowrap">
                        <font-awesome-icon :icon="['fas', 'clipboard-check']" size="lg" />&nbsp;
                        <font-awesome-icon :icon="['far', 'plus']" />
                      </span>
                    </b-button>
                  </b-button-group> -->
                </b-button-group>

              </template>
              <template v-if="displayMode === DisplayMode.NestedRevisions" #row-details="data">
                <div style="margin-left:2em">
                  <span v-if="!data.item.SurveyRevisions" class="font-weight-light">{{ $t('Webportal.Actions.NoSurveyRevisionsFound')}} </span>

                  <div v-else class="d-flex flex-wrap">
                    <survey-revision-card
                      v-for="surveyRevision in data.item.SurveyRevisions"
                      :key="surveyRevision.SurveyRevisionId"
                      :surveyRevision="surveyRevision" />
                  </div>
                </div>
              </template>

              <!-- Survey Revision columns -->
              <template v-slot:[`cell(SurveyRevision.SurveyRevisionVisibleId)`]="data">
                <template v-if="data.item.SurveyRevision">
                  <b-link :to="{ name: 'SurveyRevision', params: { id: data.item.SurveyRevision.SurveyRevisionId } }">
                    {{ data.value }}
                  </b-link>
                </template>
              </template>
              <template v-slot:[`cell(SurveyRevision.CreatedAt)`]="data">
                <template v-if="data.item.SurveyRevision">
                  {{ data.value | msm_date }}
                </template>
              </template>
              <template v-slot:[`cell(SurveyRevision.LastUpdatedAt)`]="data">
                <template v-if="data.item.SurveyRevision">
                  {{ data.item.SurveyRevision.CalendarWeek }} ({{ data.value | msm_datetime }})
                </template>
              </template>
              <template v-slot:[`cell(SurveyRevision.DueDate)`]="data">
                <template v-if="data.item.SurveyRevision">
                  {{ data.value | msm_date }}
                </template>
              </template>
              <template v-slot:[`cell(SurveyRevision.StatusId)`]="data">
                <template v-if="data.item.SurveyRevision">
                  <div class="d-block">
                    <font-awesome-icon :icon="['far', getRevisionStatusIcon(data.value)]" />
                    {{ getRevisionStatusText(data.value) }}
                  </div>
                  <div v-if="data.item.SurveyRevision.NeedsTranslation"
                       class="d-block">
                    <font-awesome-icon v-if="data.item.SurveyRevision.IsTranslated" :icon="['far', 'check-circle']" />
                    <font-awesome-icon v-else :icon="['far', 'circle']" />
                    {{ $t('Webportal.Translated') }}
                  </div>
                  <div v-if="data.item.SurveyRevision.IsHealing"
                       class="d-block">
                    <font-awesome-icon :icon="['far', 'plus-circle']" />
                    Healing
                  </div>
                </template>
              </template>
              <template v-slot:cell(SurveyRevisionAction)="data">
                <template v-if="data.item.SurveyRevision">
                  <b-button v-if="displayMode === DisplayMode.TopLevelRevisions"
                            :to="{ name: 'SurveyRevision', params: { id: data.item.SurveyRevision.SurveyRevisionId } }"
                            pill variant="primary" size="sm" class="w-100"
                            v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Actions.OpenSurveyRevision')">
                    <font-awesome-icon :icon="['far', 'edit']" />
                  </b-button>
                </template>
              </template>
              <template v-slot:[`cell(Survey.Title)`]="data">
                <span style="max-width: 30em; display: block;">{{ data.value }}</span>
                <template v-if="data.item.SurveyRevision">
                  <div class="d-flex flex-wrap pt-1 pb-1">
                    <b-badge v-if="data.item.SurveyRevision.HasRemarks || data.item.SurveyRevision.HasAttachments"
                            class="survey-revision-item font-weight-normal" variant="secondary" pill>
                      <font-awesome-icon :icon="['far', 'poll-h']" />
                      {{ $t("Webportal.Actions.SurveyRevision.General") }}
                    </b-badge>
                    <b-badge v-for="(question, index) in data.item.Questions" :key="question.QuestionNumber"
                            class="survey-revision-item font-weight-normal" variant="secondary" pill
                            v-b-tooltip.hover.nofade="toolTipDelay" :title="`${question.QuestionNumber} ${question.QuestionText}`">
                      <font-awesome-icon :icon="['far', 'question-circle']" />
                      {{ question.QuestionNumber ? question.QuestionNumber : `#${index + 1}` }}
                    </b-badge>
                  </div>
                </template>
              </template>

              <!-- Survey Improvement columns -->
              <template v-slot:[`cell(SurveyImprovement.SurveyImprovementVisibleId)`]="data">
                <template v-if="data.item.SurveyImprovement">
                  <b-link :to="{ name: 'SurveyImprovement', params: { id: data.item.SurveyImprovement.SurveyImprovementId } }">
                    {{ data.value }}
                  </b-link>
                </template>
              </template>
              <template v-slot:[`cell(SurveyImprovement.CreatedAt)`]="data">
                <template v-if="data.item.SurveyImprovement">
                  {{ data.value | msm_date }}
                </template>
              </template>
              <template v-slot:[`cell(SurveyImprovement.LastUpdatedAt)`]="data">
                <template v-if="data.item.SurveyImprovement">
                  {{ data.item.SurveyImprovement.CalendarWeek }} ({{ data.value | msm_datetime }})
                </template>
              </template>
              <template v-slot:[`cell(SurveyImprovement.DueDate)`]="data">
                <template v-if="data.item.SurveyImprovement">
                  {{ data.value | msm_date }}
                </template>
              </template>
              <template v-slot:[`cell(SurveyImprovement.StatusId)`]="data">
                <template v-if="data.item.SurveyImprovement">
                  <div class="d-block">
                    <font-awesome-icon :icon="['far', getImprovementStatusIcon(data.value)]" />
                    {{ getImprovementStatusText(data.value) }}
                  </div>
                </template>
              </template>
              <template v-slot:cell(SurveyImprovementAction)="data">
                <template v-if="data.item.SurveyImprovement">
                  <b-button v-if="displayMode === DisplayMode.TopLevelImprovements"
                            :to="{ name: 'SurveyImprovement', params: { id: data.item.SurveyImprovement.SurveyImprovementId } }"
                            pill variant="primary" size="sm" class="w-100"
                            v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Actions.OpenSurveyImprovement')">
                    <font-awesome-icon :icon="['far', 'edit']" />
                  </b-button>
                </template>
              </template>
            </b-table>
          </div>
        </b-card>
      </div>

      <div class="col-xl-5 p-0 pdf-viewer-container-xxl" v-bind:class="{ 'd-none': !isPreviewVisible }">
        <div class="card pdf-viewer col-5 p-0">
          <div>
            <button type="button" class="close" aria-label="Close" @click="closePreview()"
                    v-b-tooltip.hover.nofade="toolTipDelay"
                    :title="$t('Webportal.ClosePreview')">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="card-body pdf-viewer-height pt-0" v-bind:class="{ spinner: isPreviewLoading }">
            <iframe class="pdf-viewer-height w-100" :src="previewUrl" frameBorder="0"></iframe>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import AppConfig from '@/mixins/AppConfig';
import DownloadFileMixin from '@/mixins/DownloadFileMixin';

import SurveyFilter from '@/components/SurveyFilter-APP_TARGET';
import SearchInput from '@/components/SearchInput';
import SearchSelect from '@/components/SearchSelect';
import Pagination from '@/components/Pagination'
import SurveyInstanceMixin from '@/mixins/SurveyInstanceMixin';
import SurveyRevisionCard from '@/components/Action/SurveyRevisionCard';
import SurveyRevisionMixin from "@/mixins/SurveyRevisionMixin";
import SurveyImprovementMixin from "@/mixins/SurveyImprovementMixin";

import EventBus from '@/services/EventBus.js';
import SurveysService from '@/services/SurveysService';
import ReportsService from '@/services/ReportsService';
import ActionService from '@/services/ActionService';
import AuthenticationService from '@/services/AuthenticationService';
import AdministationService from '@/services/AdministationService';

const DisplayMode = {
  None: 0,
  TopLevelRevisions: 1,
  NestedRevisions: 2,
  TopLevelImprovements: 3
}

export default {
  name: "CheckExplorer",

  components: { SurveyFilter, SearchInput, SearchSelect, Pagination, SurveyRevisionCard },

  mixins: [ AppConfig, DownloadFileMixin, SurveyInstanceMixin, SurveyRevisionMixin, SurveyImprovementMixin ],

  props: {
    mode: null
  },

  data() {
    return {
      isLoading: false,                     // Determines if data loading is currently in progress

      // Filter properties
      clientSettings: null,
      searchText: null,                     // Full text search text

      columnFilter: {                       // Filter values for each column
        Survey: { Title: null },
        SurveyInstance : {
          ExternalSurveyInstanceId: null,
          CalendarWeek: null,
          Result: null,
          SurveyInstanceStatusId: null },
        ClientLocation: {
          ClientLocationIdOnClientSite: null,
          FullAddress: null },
        SurveyRevision: {
          SurveyRevisionId: null,
          SurveyRevisionVisibleId: null,
          CreatedAt: null,
          CalendarWeek: null,
          DueDate: null,
          StatusId: null },
        SurveyImprovement: {
          SurveyImprovementId: null,
          SurveyImprovementVisibleId: null,
          CreatedAt: null,
          CalendarWeek: null,
          StatusId: null },
        SurveyKpi1Result: null,
        SurveyKpi2Result: null
      },

      sortBy: null,                         // Sorting column
      sortDescending: false,                // Sorting order

      surveyFilter: [],
      currentFilter: null,                  // Current filter object for change detection

      DisplayMode: DisplayMode,
      displayMode: DisplayMode.None,
      onlyHasRevisions: true,

      onlyUnreleased: false,

      isExporting: false,

      // Paging properties
      selectedPage: null,                   // Number of the page, that is currently visible
      selectedPageSize: null,               // Number of items on one page of the table
      totalCount: 0,                        // Number of total items

      useSurveyDefaultLocalization: true,

      // Table properties
      items: [],

      isTargetGroupSelectionVisible: false,
      isDueDateVisible: APP_TARGET === 'db' ? true : false,

      toggleSelectionChecked: false,
      toggleSelectionIndeterminate: false,

      previewUrl: null,
      isPreviewLoading: false,
      isPreviewVisible: false,
      previewCancelToken: null,
      loadDataCancelToken: null,

      firstLoad: false
    }
  },

  computed: {
    isCalendarWeekFieldVisible() {
      return this.isMsmUser || this.items?.some(i => i.ShowCheckDate || i.ShowCheckTime) || this.columnFilter.SurveyInstance.CalendarWeek != null || this.sortBy === 'SurveyInstance.TimeStampSurveyStart';
    },

    isStatusFieldVisible() {
      return this.isMsmUser || this.items?.some(i => i.ShowCheckStatus) || this.columnFilter.SurveyInstance.SurveyInstanceStatusId != null || this.sortBy === 'SurveyInstance.SurveyInstanceStatusId';
    },

    isOverallScoreVisible() {
      return this.items?.some(i => i.SurveyInstance.Result != null) ?? true;
    },

    isKpi1Visible() {
      return this.items?.some(i => i.SurveyKpi1Result != null) || this.columnFilter.SurveyKpi1Result != null || this.sortBy === 'SurveyKpi1Result';
    },

    isKpi2Visible() {
      return this.items?.some(i => i.SurveyKpi1Result != null) || this.columnFilter.SurveyKpi2Result != null || this.sortBy === 'SurveyKpi2Result';
    },

    fields() {
      if (this.displayMode === DisplayMode.TopLevelRevisions)
        return [
          { key: 'SurveyRevision.SurveyRevisionVisibleId', label: this.$t('Webportal.Id'), thStyle: 'width:6em', sortable: true },
          { key: 'SurveyRevision.CreatedAt', label: this.$t('Webportal.Created'), class: 'text-center', thStyle: 'width:11em', sortable: true },
          { key: 'SurveyRevision.LastUpdatedAt', label: this.$t('Webportal.LastUpdated', { at: ''}), class: 'text-center', thStyle: 'width:11em', sortable: true },
          ... this.isDueDateVisible ? [{ key: 'SurveyRevision.DueDate', label: this.$t('Webportal.Due'), class: 'text-center', thStyle: 'width:11em', sortable: true }] : [],
          { key: 'SurveyRevision.StatusId', label: this.$t('Webportal.StatusCheckRevision'), thAttr: { colspan: 2 }, thClass: 'text-left', thStyle: 'width:7em', tdClass: 'check-revision-status', sortable: true },
          { key: 'SurveyRevisionAction', label: '', thClass: 'd-none', thStyle: 'width:4em', tdClass: 'check-revision-action' },
          { key: 'Survey.Title', label: this.$t('Webportal.Survey'), thStyle: '', sortable: true },
          { key: 'SurveyInstance.ExternalSurveyInstanceId', label: this.$t('Webportal.SurveyInstanceId'), thStyle: 'width:7em', sortable: true },
          { key: 'ClientLocation.ClientLocationIdOnClientSite', label: this.$t('Webportal.LocationId'), thStyle: 'width:8em', sortable: true },
          { key: 'ClientLocation', label: this.$t('Webportal.LocationAddress'), thStyle: '', sortable: true },
          ... this.isStatusFieldVisible ? [{ key: 'SurveyInstance.SurveyInstanceStatusId', label: this.$t('Webportal.StatusCheck'), thAttr: { colspan: 2 }, thClass: 'text-left', thStyle: 'width:6em', tdClass: 'check-status', sortable: true }] : [],
          { key: 'Action', label: '', thClass: this.isStatusFieldVisible ? 'd-none' : '', thStyle: 'width:6em', tdClass: 'check-action' }
        ];
      else if (this.displayMode === DisplayMode.TopLevelImprovements)
        return [
          { key: 'SurveyImprovement.SurveyImprovementVisibleId', label: this.$t('Webportal.Id'), thStyle: 'width:6em', sortable: true },
          { key: 'SurveyImprovement.CreatedAt', label: this.$t('Webportal.Created'), class: 'text-center', thStyle: 'width:11em', sortable: true },
          { key: 'SurveyImprovement.LastUpdatedAt', label: this.$t('Webportal.LastUpdated', { at: ''}), class: 'text-center', thStyle: 'width:11em', sortable: true },
          { key: 'SurveyImprovement.StatusId', label: this.$t('Webportal.StatusCheckImprovement'), thAttr: { colspan: 2 }, thClass: 'text-left', thStyle: 'width:13em', tdClass: 'check-improvement-status', sortable: true },
          { key: 'SurveyImprovementAction', label: '', thClass: 'd-none', thStyle: 'width:4em', tdClass: 'check-improvement-action' },
          { key: 'Survey.Title', label: this.$t('Webportal.Survey'), thStyle: '', sortable: true },
          { key: 'SurveyInstance.ExternalSurveyInstanceId', label: this.$t('Webportal.SurveyInstanceId'), thStyle: 'width:7em', sortable: true },
          { key: 'ClientLocation.ClientLocationIdOnClientSite', label: this.$t('Webportal.LocationId'), thStyle: 'width:8em', sortable: true },
          { key: 'ClientLocation', label: this.$t('Webportal.LocationAddress'), thStyle: '', sortable: true },
          ... this.isStatusFieldVisible ? [{ key: 'SurveyInstance.SurveyInstanceStatusId', label: this.$t('Webportal.StatusCheck'), thAttr: { colspan: 2 }, thClass: 'text-left', thStyle: 'width:6em', tdClass: 'check-status', sortable: true }] : [],
          { key: 'Action', label: '', thClass: this.isStatusFieldVisible ? 'd-none' : '', thStyle: 'width:6em', tdClass: 'check-action' }
        ];
      else
        return [
          { key: 'Select', thStyle: 'width:2em' },
          { key: 'Survey.Title', label: this.$t('Webportal.Survey'), thStyle: '', sortable: true },
          { key: 'SurveyInstance.ExternalSurveyInstanceId', label: this.$t('Webportal.SurveyInstanceId'), thStyle: 'width:7em', sortable: true },
          { key: 'ClientLocation.ClientLocationIdOnClientSite', label: this.$t('Webportal.LocationId'), thStyle: 'width:8em', sortable: true },
          { key: 'ClientLocation', label: this.$t('Webportal.LocationAddress'), thStyle: '', sortable: true },
          ... this.isCalendarWeekFieldVisible ? [{ key: 'SurveyInstance.TimeStampSurveyStart', label: this.$t('Webportal.CalendarWeek'), class: 'text-center', thStyle: 'width:11em', sortable: true }] : [],
          ... this.isOverallScoreVisible ? [{ key: 'SurveyInstance.Result', label: this.$t('Webportal.Result'), class: 'text-right', thStyle: 'width:6em', sortable: true }] : [],
          ... this.isKpi1Visible ? [{ key: 'SurveyKpi1Result', class: 'text-right', thStyle: 'width:6em', sortable: true }] : [],
          ... this.isKpi2Visible ? [{ key: 'SurveyKpi2Result', class: 'text-right', thStyle: 'width:6em', sortable: true }] : [],
          ... this.isStatusFieldVisible ? [{ key: 'SurveyInstance.SurveyInstanceStatusId', label: this.$t('Webportal.Status'), thStyle: 'width:6em', thClass: 'text-left', tdClass: 'text-left', sortable: true }] : [],
          { key: 'Action', label: '', thStyle: 'width:6em', tdClass: 'text-right' }
        ];
    },

    kpiToolTipItems() {
      return _(this.items)
        .groupBy('Survey.SurveyId')
        .filter(surveyInstances => _.first(surveyInstances).SurveyKpi1Name != null || _.first(surveyInstances).SurveyKpi2Name != null)
        .map(surveyInstances => ({
          SurveyId: _.first(surveyInstances).Survey.SurveyId,
          SurveyTitle: _.first(surveyInstances).Survey.Title,
          SurveyKpi1Name: _.first(surveyInstances).SurveyKpi1Name,
          SurveyKpi2Name: _.first(surveyInstances).SurveyKpi2Name
          }))
        .value();
    },

    filter() {
      // Creates a new object without empty values from source
      const isNotEmptyPredicate = o => !_.isNil(o);
      const pickByDeep = (obj, fn) =>
        _.pickBy( // Remove on root level
          _.mapValues(obj, (val, key) =>
            !_.isObjectLike(val) ? val : // Take plain values
              !_.isEmpty(_.pickBy(val, fn)) ? _.pickBy(val, fn) : null) // Map values of nested object
          , fn)

      return {
        clientIds: this.surveyFilter.clientIds,
        projectIds: this.surveyFilter.projectIds,
        surveyIds: this.surveyFilter.surveyIds,
        waveIds: this.surveyFilter.waveIds,
        scenarioIds: this.surveyFilter.scenarioIds,
        propertyIds: this.surveyFilter.propertyIds,
        locationIds: this.surveyFilter.clientLocationIds,
        searchText: this.searchText,
        columnFilter: pickByDeep(this.columnFilter, isNotEmptyPredicate),
        revisionDisplayMode: this.displayMode, // Ensure filter update on display mode change
        withRevisions: this.displayMode === DisplayMode.TopLevelRevisions || this.displayMode == DisplayMode.NestedRevisions,
        onlyHasRevisions: this.displayMode === DisplayMode.TopLevelRevisions || this.displayMode == DisplayMode.NestedRevisions ? this.onlyHasRevisions : null,
        withImprovements: this.displayMode === DisplayMode.TopLevelImprovements,
        onlyUnreleased: this.displayMode === DisplayMode.None ? this.onlyUnreleased : null,
        sortBy: this.sortBy,
        sortDescending: this.sortDescending
      };
    },

    options() {
      let result = {
        page: this.selectedPage,
        pageSize: this.selectedPageSize,
        propertyUseLogicalAnd: this.surveyFilter.propertyUseLogicalAnd
      }

      return result;
    },

    selectedSurveyInstances() {
      return this.items.filter(item => item.Selected);
    },

    selectedUnreleasedSurveyInstances() {
      return this.selectedSurveyInstances.filter(item => this.isReleaseCheckEnabled(item) && !this.isCheckReleased(item));
    },

    downloadDealerReportsDisabled() {
      return _.isEmpty(this.items) || _.isEmpty(this.selectedSurveyInstances);
    },

    releaseChecksDisabled() {
      return _.isEmpty(this.items) || _.isEmpty(this.selectedUnreleasedSurveyInstances);
    },

    isMsmUser() {
      return AuthenticationService.isMSMUser;
    },

    userHasReleaseCheckPermission: function() {
      return this.isMsmUser || AuthenticationService.localUserProfile.Permissions.includes('ReleaseCheck');
    },

    surveyInstanceStatusOptions() {
      let result = this.getSurveyInstanceStatusOptions;
      
      [
          { value: '220', text: this.$t('Webportal.StatusOK') }
      ]

      if (!this.isMsmUser)
      {
        return result.filter(s => s.value == this.SurveyInstanceStatus.Released);
      }

      return result;
    },

    surveyRevisionStatusOptions() {
      let result = this.getRevisionStatusOptions;

      if (this.clientSettings)
      {
        let allAllowedSurveyRevisionStatuses = _.uniq([...this.clientSettings.values()].flatMap(v => v.AllowedSurveyRevisionStatuses));
        return result.filter(s => allAllowedSurveyRevisionStatuses.includes(s.value));
      }
      else
        return result;
    },

    surveyImprovementStatusOptions() {
      return this.getImprovementStatusOptions;
    },
  },

  mounted() {
    this.initializeTheme();

    this.displayMode =
      this.userHasActionRevisePermission() && (this.mode === 'revision') ? DisplayMode.TopLevelRevisions :
      this.userHasActionImprovementPermission() && (this.mode === 'improvement') ? DisplayMode.TopLevelImprovements : DisplayMode.None;

    this.getClientSettings();
    EventBus.$on('LocalizationChanged', this.localizationChanged);
  },

  beforeDestroy() {
    EventBus.$off('LocalizationChanged', this.localizationChanged);
  },

  methods: {
    toggleSurveyInstanceReadByUser(SurveyInstance) {
      SurveysService.postSurveyInstanceReadByUser(SurveyInstance.SurveyInstanceId)
      .then(() => {
        SurveyInstance.IsReadByUser = !SurveyInstance.IsReadByUser;
      });
    },
    getLocationActiveText() {
      return this.$t('Webportal.Location') + ' ' + this.$t('Webportal.Active');
    },

    getLocationInactiveText() {
      return this.$t('Webportal.Location') + ' ' + this.$t('Webportal.Inactive');
    },

    getLastSubmittedPerEMailFormatted(item) {
      let retVal = this.$t('Webportal.MailWillBeSent');
      if (item.LastSubmittedPerEMail) {
        retVal = this.$t('Webportal.MailSent') + ' ' + this.$options.filters.msm_date(item.LastSubmittedPerEMail);
      }
      return retVal;
    },

    getSurveyInstanceLanguage(item, useSurveyDefault) {
      return useSurveyDefault === false &&
        item.ClientLocation.DefaultLocalization &&
        item.ClientLocation.DefaultLocalization.includes('-') ?
        item.ClientLocation.DefaultLocalization.split('-')[1].toLowerCase() :
        item.Survey.DefaultLocalization.split('-')[1].toLowerCase();
    },

    localizationChanged(localizationId) {
      this.loadDataInternal();
    },

    getClientSettings() {
      AdministationService.getClientSettings()
        .then(response => {
          this.clientSettings = response.data ? new Map(response.data.map(item => [item.ClientId, item])) : null;

          // Show target group selection only if one of the clients the user has permission for is configured to show it
          this.isTargetGroupSelectionVisible = this.isMsmUser || response.data && response.data.some(cs => cs.ShowTargetGroupSelection);
        });
    },

    // Gets survey instances data for the table from the server, including current filter and pagination
    getSurveyInstances() {
      this.isLoading = true;

      if (this.loadDataCancelToken)
        this.loadDataCancelToken.cancel();
      this.loadDataCancelToken = axios.CancelToken.source();

      SurveysService.getSurveyInstances(this.filter, this.options, this.loadDataCancelToken)
        .then(response => {
          this.totalCount = response.data.TotalCount;
          this.items = response.data.items;
          this.toggleSelectionChecked = false;
          this.toggleSelectionIndeterminate = false;

          if (this.displayMode === DisplayMode.NestedRevisions && this.items)
            this.items.forEach(e => e._showDetails = true);
            
          this.isLoading = false;
        })
        .catch((error) => {
          if (!axios.isCancel(error)) 
          {
            this.isLoading = false;
            throw error;
          }
        });
    },

    getSurveyRevisions() {
      this.isLoading = true;

      if (this.loadDataCancelToken)
        this.loadDataCancelToken.cancel();
      this.loadDataCancelToken = axios.CancelToken.source();

      ActionService.getSurveyRevisions(this.filter, this.options, this.loadDataCancelToken)
        .then(response => {
          this.totalCount = response.data.TotalCount;
          this.items = response.data.items;
          this.toggleSelectionChecked = false;
          this.toggleSelectionIndeterminate = false;
          
          this.isLoading = false;
        })
        .catch((error) => {
          if (!axios.isCancel(error)) 
          {
            this.isLoading = false;
            throw error;
          }
        });
    },

    getSurveyImprovements() {
      this.isLoading = true;

      if (this.loadDataCancelToken)
        this.loadDataCancelToken.cancel();
      this.loadDataCancelToken = axios.CancelToken.source();

      ActionService.getSurveyImprovements(this.filter, this.options, this.loadDataCancelToken)
        .then(response => {
          this.totalCount = response.data.TotalCount;
          this.items = response.data.items;
          this.toggleSelectionChecked = false;
          this.toggleSelectionIndeterminate = false;
          
          this.isLoading = false;
        })
        .catch((error) => {
          if (!axios.isCancel(error)) 
          {
            this.isLoading = false;
            throw error;
          }
        });
    },


    // Applies changed filter and triggeres data loading if necessary
    loadData(callSource = '') {
      // Only load data when effective filter has changed
      // TODO: The order of array elements shouldn't matter when comparing
      if (!_.isEqual(this.filter, this.currentFilter)) {
        this.currentFilter = this.filter;

        this.$refs.pagination.resetPaging();

        if (callSource !== 'mounted') {
          this.loadDataInternal();
        }
      }
    },

    loadDataInternal(source) {
      if (!this.firstLoad && source === 'input') {
        return;
      }
      this.firstLoad = true;

      if (this.displayMode === DisplayMode.TopLevelRevisions)
        this.getSurveyRevisions();
      else if (this.displayMode === DisplayMode.TopLevelImprovements)
        this.getSurveyImprovements();
      else
        this.getSurveyInstances();
    },

    changeDisplayMode(value)
    {
      this.displayMode = value;
      this.loadData();
    },

    isSurveyInstanceProcessed(item) {
      return item.SurveyInstance.SurveyInstanceStatusId > 215 &&
             item.SurveyInstance.SurveyInstanceStatusId < 300
    },

    sortChanged(e) {
      this.sortBy = e.sortBy;
      this.sortDescending = e.sortDesc;
      this.loadData();
    },

    // Selects a row when clicked
    rowClicked(item) {
      if (this.displayMode === DisplayMode.TopLevelRevisions || this.displayMode === DisplayMode.TopLevelImprovements || !this.isSurveyInstanceProcessed(item))
          return;

      let checked = item.Selected ? !item.Selected : true;

      this.updateToggleSelection(checked);

      // Use $set to register the new property for vue tracking
      this.$set(item, 'Selected', checked);
    },

    toggleSelection(checked) {
      _.forEach(this.items, item => {
          if (this.isSurveyInstanceProcessed(item)) {
            this.$set(item, 'Selected', checked);
          }
        });
    },

    updateToggleSelection(checked) {
      let count = 0;

      _.forEach(this.items, item => {
          if (item.Selected) count++;
        });

      // New value is not written to property right now
      checked ? count++ : count--;

      // Handle changes in individual flavour checkboxes
      if (count === 0) {
        this.toggleSelectionIndeterminate = false;
        this.toggleSelectionChecked = false;
      } else if (count === this.items.length) {
        this.toggleSelectionIndeterminate = false;
        this.toggleSelectionChecked = true;
      } else {
        this.toggleSelectionIndeterminate = true;
        this.toggleSelectionChecked = false;
      }
    },

    downloadCheckRevisions() {
      this.isExporting = true;

      ReportsService.getCheckRevisionExport({
          surveyIds: this.surveyFilter.computedSurveyIds,
          waveIds: this.surveyFilter.waveIds,
          scenarioIds: this.surveyFilter.scenarioIds,
          locationIds: this.surveyFilter.clientLocationIds,
          propertyIds: this.surveyFilter.propertyIds,
          propertyUseLogicalAnd: this.surveyFilter.propertyUseLogicalAnd,
          searchText: this.searchText
        }, { 
          includeCustomProperties: true
        })
        .then(response => {
          this.isExporting = false;

          if (response.status === 204)
            this.$bvToast.toast(
              this.$t("Webportal.NoDataFound"),
              {
                autoHideDelay: 5 * 1000,
                variant: "warning",
                noCloseButton: true,
              }
            );
          else
            this.downloadFile(response);
        })
        .catch(_ => { this.isExporting = false; } );
    },

    downloadBonusReport() {
      this.isExporting = true;

      ReportsService.getBonusReport({
          surveyIds: this.surveyFilter.computedSurveyIds,
          waveIds: this.surveyFilter.waveIds,
          scenarioIds: this.surveyFilter.scenarioIds,
          locationIds: this.surveyFilter.clientLocationIds,
          propertyIds: this.surveyFilter.propertyIds
        })
        .then(response => {
          this.isExporting = false;

          if (response.status === 204)
            this.$bvToast.toast(
              this.$t("Webportal.NoDataFound"),
              {
                autoHideDelay: 5 * 1000,
                variant: "warning",
                noCloseButton: true,
              }
            );
          else
            this.downloadFile(response);
        })
        .catch(_ => { this.isExporting = false; } );
    },

    downloadDealerReports() {
      this.isExporting = true;
      ReportsService.getCheckReports(
        this.selectedSurveyInstances.map(si => si.SurveyInstance.SurveyInstanceId),
        { useSurveyDefaultLocalization: this.useSurveyDefaultLocalization })
        .then(response => {
          this.downloadFile(response);
          this.isExporting = false;
          this.selectedSurveyInstances.forEach(si => {
            if(!si.SurveyInstance.IsReadByUser) {
              this.toggleSurveyInstanceReadByUser(si.SurveyInstance);
            }
          })
        })
        .catch(_ => { this.isExporting = false; } );
    },

    showPreview(item, useSurveyDefault, targetGroup) {
      let options = {
        localizationId: !useSurveyDefault ? item.ClientLocation.DefaultLocalization : null,
        targetGroup: targetGroup
      };

      // Check for pdf-viewer-breakpoint
      if (window.innerWidth < 1600)
      {
        // Open in new tab
        var win = window.open('', '_blank');
        win.document.title = `Check Report ${item.SurveyInstance.ExternalSurveyInstanceId}`;
        win.focus();

        ReportsService.getCheckReport(item.SurveyInstance.SurveyInstanceId, options)
          .then((response) => {
            const blobUrl = window.URL.createObjectURL(response.data);
            win.location.href = `${process.env.BASE_URL}static/pdfjs/web/viewer.html?file=${blobUrl}`;

            // mark surveyInstance as read if it was unread
            if(!item.SurveyInstance.IsReadByUser) {
              this.toggleSurveyInstanceReadByUser(item.SurveyInstance);
            }
          });
      }
      else
      {
        // Open in preview window
        if (this.previewCancelToken)
          this.previewCancelToken.cancel('showPreview');

        this.previewCancelToken = axios.CancelToken.source();

        this.isPreviewLoading = true;
        this.isPreviewVisible = true;

        ReportsService.getCheckReport(item.SurveyInstance.SurveyInstanceId, options, this.previewCancelToken)
          .then((response) => {
            this.isPreviewLoading = false;

            const blobUrl = window.URL.createObjectURL(response.data);
            this.previewUrl =`${process.env.BASE_URL}static/pdfjs/web/viewer.html?file=${blobUrl}`;

            // mark surveyInstance as read if it was unread
            if(!item.SurveyInstance.IsReadByUser) {
              this.toggleSurveyInstanceReadByUser(item.SurveyInstance);
            }
          })
          .catch((thrown) => {
            if (thrown.message != 'showPreview')
            {
              this.previewUrl = null;
              this.isPreviewLoading = false;
            }
          });
      }
    },

    createSurveyRevision(item) {
      this.$router.push({ name: 'SurveyRevision', query: { sid: item.SurveyInstance.SurveyInstanceId } })
    },

    createSurveyImprovement(item) {
      this.$router.push({ name: 'SurveyImprovement', query: { sid: item.SurveyInstance.SurveyInstanceId } })
    },

    releaseCheck(item) {
      this.$bvModal
        .msgBoxConfirm(
          this.$t("Webportal.Dialog.ConfirmReleaseOfCheck.Text"),
          {
            title: this.$t("Webportal.Dialog.ConfirmReleaseOfCheck.Title"),
            okTitle: this.$t("Webportal.Dialog.Ok"),
            cancelTitle: this.$t("Webportal.Dialog.Cancel"),
            hideHeaderClose: false,
          }
        )
        .then((value) => {
          if (value) {
            this.isLoading = true;

            SurveysService.setSurveyInstanceReleasedState([ item.SurveyInstance.SurveyInstanceId ])
              .then(_ => {
                if (!this.onlyUnreleased)
                  item.SurveyInstance.IsReleased = true;
                else
                  this.loadDataInternal();

                this.$bvToast.toast(
                  this.$t("Webportal.SaveSuccess.Text", {
                    entity: this.$t("Webportal.Check"),
                  }),
                  {
                    autoHideDelay: 2000,
                    variant: "success",
                    noCloseButton: true,
                  }
                );
              })
              .catch((error) => {
                console.error(error);

                this.$bvToast.toast(
                  this.$t("Webportal.SaveFailed.Text", {
                    entity: this.$t("Webportal.Check"),
                  }),
                  {
                    autoHideDelay: 5000,
                    variant: "danger",
                    noCloseButton: true,
                  }
                );
              })
              .finally(_ => {
                this.isLoading = false;
              });
          }
        });
    },
    
    releaseSelectedChecks() {
      this.$bvModal
        .msgBoxConfirm(
          this.$t("Webportal.Dialog.ConfirmReleaseOfChecks.Text"),
          {
            title: this.$t("Webportal.Dialog.ConfirmReleaseOfChecks.Title"),
            okTitle: this.$t("Webportal.Dialog.Ok"),
            cancelTitle: this.$t("Webportal.Dialog.Cancel"),
            hideHeaderClose: false,
          }
        )
        .then((value) => {
          if (value) {
            this.isLoading = true;

            SurveysService.setSurveyInstanceReleasedState(
              this.selectedUnreleasedSurveyInstances.map(si => si.SurveyInstance.SurveyInstanceId))
              .then(_ => { 
                this.loadDataInternal();
                
                this.$bvToast.toast(
                  this.$t("Webportal.SaveSuccess.Text", {
                    entity: this.$t("Webportal.Check"),
                  }),
                  {
                    autoHideDelay: 2000,
                    variant: "success",
                    noCloseButton: true,
                  }
                );
              })
              .catch((error) => {
                console.error(error);

                this.$bvToast.toast(
                  this.$t("Webportal.SaveFailed.Text", {
                    entity: this.$t("Webportal.Check"),
                  }),
                  {
                    autoHideDelay: 5000,
                    variant: "danger",
                    noCloseButton: true,
                  }
                );
              })
              .finally(_ => {
                this.isLoading = false;
              });
          }
        });
    },

    closePreview() {
      this.isPreviewLoading = false;
      this.isPreviewVisible = false;
      this.previewUrl = null;

      if (this.previewCancelToken)
        this.previewCancelToken.cancel('closePreview');
    },

    calendarWeekAndDateFormatted(item) {
      if (!item.SurveyInstance.TimeStampSurveyStart)
        return '';

      if (this.isMsmUser || (item.ShowCheckDate && item.ShowCheckTime))
        return item.SurveyInstance.CalendarWeek + ' (' + this.$options.filters.msm_datetime(item.SurveyInstance.TimeStampSurveyStart) + ')';
      else if (item.ShowCheckDate)
        return item.SurveyInstance.CalendarWeek + ' (' + this.$options.filters.msm_date(item.SurveyInstance.TimeStampSurveyStart) + ')';
      else if (item.ShowCheckTime)
        return item.SurveyInstance.CalendarWeek + ' (' + this.$options.filters.msm_time(item.SurveyInstance.TimeStampSurveyStart) + ')';
      else
        return item.SurveyInstance.CalendarWeek
    },

    isReleaseCheckEnabled(item) {
      let cs = this.clientSettings ? this.clientSettings.get(item.Client.ClientId) : null;

      return cs?.IsReleaseCheckEnabled ?? false;
    },

    isCheckReleased(item) {
      return item.SurveyInstance.IsReleased;
    }
  }
};
</script>

<style lang="scss">
  #check-table  {
    .b-table-details {
      background-color: transparent;
    }

    .check-revision-status {
      width: 7em;
      text-align: left;
    }

    .check-revision-action {
      width: 4em;
      text-align: right;
    }

    .check-improvement-status {
      width: 7em;
      text-align: left;
    }

    .check-improvement-action {
      width: 4em;
      text-align: right;
    }

    .check-status {
      width: 6em;
      text-align: left;
    }

    .check-action {
      width: 6em;
      text-align: right;
    }
  }

  #tooltip-kpi1 .tooltip-inner,
  #tooltip-kpi2 .tooltip-inner  {
    max-width: 100%;
  }
</style>
